import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { telefoneWhatsappValido } from "@shared/mercado";

/**
 * A criação de conta a partir dos DOIS links do comprador.
 *
 *   `alvo="produtor"`  (`/p/:token/criar-acesso`) → org PRODUTOR. O registro que
 *     o comprador fez vira VENDA RASCUNHO no Cacho dele, pronta para conferir,
 *     emitir e formalizar de volta.
 *   `alvo="destino"`   (`/c/:token/criar-acesso`) → org COMPRADOR perfil
 *     DISTRIBUIDOR, ligada ao destino da carga.
 *
 * ⚠️ UMA TELA, DOIS ALVOS, e não duas telas: os campos são os mesmos (nome,
 * WhatsApp, senha) e o que muda é a rota e a frase. Duas cópias divergiriam na
 * primeira validação nova.
 *
 * ⚠️ NÃO PERGUNTA O PERFIL, ao contrário do claim do C2. Lá o link é de romaneio
 * e os dois papéis cabem; aqui o TIPO DO LINK já respondeu — quem chega por um
 * link de carga é quem recebe a carga, quem chega por um link de registro é o
 * produtor. Perguntar o óbvio é um degrau a mais para perder gente.
 */
export default function AdotarPage({ alvo }: { alvo: "produtor" | "destino" }) {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cidade, setCidade] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");

  const ehProdutor = alvo === "produtor";
  const rota = ehProdutor
    ? `/api/publico/item/${token}/adotar`
    : `/api/publico/carga/${token}/adotar`;

  const adotar = useMutation({
    mutationFn: () => apiRequest("POST", rota, {
      nome: nome.trim(),
      telefone,
      senha,
      email: email.trim() || undefined,
      cidade: cidade.trim() || undefined,
    }).then(r => r.json()),
    onSuccess: () => {
      // A conta já nasce logada (cookie no 201) — mandar para o login logo
      // depois de escolher a senha é perder gente no degrau mais bobo.
      setLocation(ehProdutor ? "/cacho/vendas" : "/mercado");
    },
    onError: (e: Error) => toast({
      title: "Não consegui criar a conta",
      description: e.message,
      variant: "destructive",
    }),
  });

  const telefoneOk = telefoneWhatsappValido(telefone);
  const podeEnviar = nome.trim().length >= 2 && telefoneOk && senha.length >= 6;

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto w-full max-w-[480px] bg-background min-h-screen p-4 space-y-5">
        <div className="space-y-1 pt-4">
          <h1 className="text-xl font-bold">Criar meu acesso</h1>
          <p className="text-sm text-muted-foreground">
            {ehProdutor
              ? "A carga que você acabou de conferir já entra como venda no seu Cacho, pronta para emitir o romaneio."
              : "Você passa a ver todas as cargas que este comprador manda para você."}
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="adotar-nome">{ehProdutor ? "Seu nome ou da fazenda" : "Nome do seu negócio"}</Label>
            <Input
              id="adotar-nome" value={nome} onChange={e => setNome(e.target.value)}
              autoComplete="organization" data-testid="input-adotar-nome"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="adotar-telefone">WhatsApp</Label>
            <Input
              id="adotar-telefone" inputMode="tel" value={telefone}
              onChange={e => setTelefone(e.target.value)}
              placeholder="(74) 99999-8888" autoComplete="tel"
              data-testid="input-adotar-telefone"
            />
            {/* O telefone é o IDENTIFICADOR DE LOGIN: muita gente da praça não
                tem e-mail de uso corrente (é a razão de `users.email` ter perdido
                o NOT NULL na 055). */}
            <p className="text-xs text-muted-foreground">
              É com ele que você vai entrar depois.
            </p>
            {telefone && !telefoneOk && (
              <p className="text-xs text-destructive">Informe DDD + 9 dígitos.</p>
            )}
          </div>

          {ehProdutor && (
            <div className="space-y-2">
              <Label htmlFor="adotar-cidade">Cidade</Label>
              <Input id="adotar-cidade" value={cidade} onChange={e => setCidade(e.target.value)} />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="adotar-email">E-mail (opcional)</Label>
            <Input
              id="adotar-email" type="email" value={email}
              onChange={e => setEmail(e.target.value)} autoComplete="email"
            />
            <p className="text-xs text-muted-foreground">Só para recuperar a senha.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adotar-senha">Senha</Label>
            <Input
              id="adotar-senha" type="password" value={senha}
              onChange={e => setSenha(e.target.value)} autoComplete="new-password"
              data-testid="input-adotar-senha"
            />
            <p className="text-xs text-muted-foreground">No mínimo 6 caracteres.</p>
          </div>
        </div>

        <Button
          className="w-full" onClick={() => adotar.mutate()}
          disabled={!podeEnviar || adotar.isPending}
          data-testid="button-adotar"
        >
          {adotar.isPending ? "Criando…" : "Criar acesso"}
        </Button>

        <Button variant="ghost" className="w-full" onClick={() => setLocation(`/${ehProdutor ? "p" : "c"}/${token}`)}>
          Voltar
        </Button>
      </div>
    </div>
  );
}
